import { useTranslation } from 'react-i18next';

import { EQUIPMENT_ICONS, MUSCLE_ICONS } from '../../utils/exerciseIcons';

import { THUMBS_URL } from './landingData';

/** A library thumbnail on its light tile: white-ground art multiplied onto it, as in the app */
export const ExerciseArt = ({ file, className = '', eager = false }) => (
  <span className={`lp-art ${className}`.trim()}>
    <img src={`${THUMBS_URL}${file}`} alt="" width="180" height="180" loading={eager ? 'eager' : 'lazy'} decoding="async" />
  </span>
);

/** Status after a title (The Status After Title Rule): green for Today, neutral for an example, green-ink fill for Pro */
export const Pill = ({ kind = 'status', children }) => <span className={`lp-pill lp-pill--${kind}`}>{children}</span>;

/** "Quadríceps · Barra", each after its line pictogram, as every exercise row in the app reads */
export const WhatLine = ({ muscle, equipment }) => {
  const { t } = useTranslation();
  return (
    <span className="lp-what">
      <span className="lp-what__part">
        <img src={MUSCLE_ICONS[muscle]} alt="" width="22" height="22" loading="lazy" decoding="async" />
        {t(`exerciseFilters.muscleGroups.${muscle}`)}
      </span>
      <span className="lp-what__dot" aria-hidden="true">
        ·
      </span>
      <span className="lp-what__part">
        <img src={EQUIPMENT_ICONS[equipment]} alt="" width="22" height="22" loading="lazy" decoding="async" />
        {t(`exerciseFilters.equipmentCategories.${equipment}`)}
      </span>
    </span>
  );
};

/**
 * A progress ring in the app's language: brand-green arc over the track from 12 o'clock, round caps, with whatever
 * goes in the middle as children. Decorative: the numbers it stands for are in the text.
 */
export const Ring = ({ share, size, stroke, className = '', children }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.min(Math.max(share || 0, 0), 1);
  return (
    <span className={`lp-ring ${className}`.trim()} style={{ width: size, height: size }}>
      <svg className="lp-ring__svg" width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
        <circle className="lp-ring__track" cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={stroke} />
        {fraction > 0 && (
          <circle
            className="lp-ring__arc"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - fraction)}
          />
        )}
      </svg>
      {children}
    </span>
  );
};

/** A section's title and its lead line, with an optional pill after the title */
export const SectionHead = ({ id, title, sub, pill, center = false }) => (
  <div className={`lp-head${center ? ' lp-head--center' : ''}`}>
    <h2 id={id} className="lp-h2">
      {title}
      {pill}
    </h2>
    {sub && <p className="lp-lead">{sub}</p>}
  </div>
);
